import { SettingHeader, Button } from "components";
import { useRef, useState } from "react";
import { createSupport } from "apis/support.api";
import { getMySupportList } from "apis/support.api";
import { useAlertStore } from "store";
import { useQuery, useQueryClient } from "react-query";

const ContactSettingComponent = () => {
  const showAlert = useAlertStore((state) => state.showAlert);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  // 내 문의 목록
  const { data: supportData } = useQuery(
    ["mySupportList"],
    () => getMySupportList(),
    { refetchOnWindowFocus: false }
  );
  const mySupportList = supportData?.data?.data || [];

  const handleSendClick = () => {
    if (title && content) {
      // 기존 API는 titleImage, contentImage 두 개만 받으므로 첫 2개 파일을 전달
      const titleFile = files[0] || null;
      const contentFile = files[1] || null;
      createSupport(title, content, titleFile, contentFile)
        .then(() => {
          showAlert("문의사항 전송완료", "success");
          clearInputArea();
          queryClient.invalidateQueries(["mySupportList"]);
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      showAlert("제목과 내용을 입력하세요", "error");
    }
  };

  const clearInputArea = () => {
    setTitle("");
    setContent("");
    setFiles([]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles) {
      const newFiles = Array.from(selectedFiles);
      setFiles((prev) => [...prev, ...newFiles]);
    }
    // input 값 리셋 (같은 파일 다시 선택 가능)
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div className="flex flex-col w-full border-achromatic-100 border-r">
      <div>
        <SettingHeader title="고객문의" />
      </div>
      <div className="flex py-6">
        <div className="flex flex-col w-full gap-8 p-4">
          {/* 문의 작성 폼 */}
          <div className="flex flex-col gap-5">
            {/* 제목 */}
            <div className="flex flex-col gap-2 w-full">
              <div className="text-sm font-semibold text-achromatic-500">제목</div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="문의 제목을 입력하세요"
                className="w-full h-14 px-4 border border-achromatic-200 rounded-xl text-sm font-medium text-achromatic-800
                  placeholder:text-achromatic-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all"
              />
            </div>

            {/* 내용 */}
            <div className="flex flex-col gap-2 w-full">
              <div className="text-sm font-semibold text-achromatic-500">내용</div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="문의 내용을 자세히 작성해주세요"
                className="w-full p-4 border border-achromatic-200 rounded-xl text-sm font-medium text-achromatic-800
                  placeholder:text-achromatic-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-all resize-none"
                style={{ minHeight: "200px" }}
              />
            </div>

            {/* 첨부파일 */}
            <div className="flex flex-col gap-2 w-full">
              <div className="text-sm font-semibold text-achromatic-500">첨부파일</div>

              {/* 파일 추가 버튼 */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 w-full h-12 px-4 border-2 border-dashed border-achromatic-200 rounded-xl
                  text-achromatic-400 hover:border-primary-300 hover:text-primary-500 hover:bg-primary-50/30 transition-all"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                <span className="text-[13px] font-medium">파일 추가 (이미지)</span>
              </button>

              {/* 첨부된 파일 목록 */}
              {files.length > 0 && (
                <div className="flex flex-col gap-1.5 mt-1">
                  {files.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between px-3 py-2 bg-achromatic-50 rounded-lg group"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {/* 이미지 미리보기 */}
                        <div className="w-8 h-8 rounded-md overflow-hidden shrink-0 bg-achromatic-200">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[12px] font-medium text-achromatic-700 truncate max-w-[200px]">{file.name}</div>
                          <div className="text-[10px] text-achromatic-400">{formatFileSize(file.size)}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-achromatic-400
                          hover:bg-red-50 hover:text-red-500 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="w-full">
            <Button text="보내기" onClick={handleSendClick} />
          </div>

          {/* 내 문의 목록 */}
          {mySupportList.length > 0 && (
            <div className="flex flex-col gap-3 mt-4">
              <div className="text-sm font-semibold text-achromatic-600">내 문의 내역</div>
              <div className="flex flex-col gap-2">
                {mySupportList.map((item: any) => (
                  <div key={item.id} className="border border-achromatic-100 rounded-xl overflow-hidden">
                    {/* 문의 헤더 */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-achromatic-50/50 transition-colors"
                      onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${
                          item.isAnswered ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                        }`}>
                          {item.isAnswered ? '답변완료' : '대기중'}
                        </span>
                        <span className="text-[13px] font-semibold text-achromatic-800 truncate">{item.title}</span>
                      </div>
                      <span className="text-[11px] text-achromatic-400 shrink-0 ml-2">
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString('ko-KR') : ''}
                      </span>
                    </div>

                    {/* 확장된 상세 내용 */}
                    {expandedItem === item.id && (
                      <div className="border-t border-achromatic-100 p-4 bg-achromatic-50/30">
                        {/* 내 문의 */}
                        <div className="mb-3">
                          <div className="text-[11px] font-bold text-achromatic-400 uppercase tracking-wider mb-1">문의 내용</div>
                          <div className="text-[13px] text-achromatic-700 leading-relaxed whitespace-pre-wrap">{item.content}</div>
                        </div>

                        {/* 답변 */}
                        {item.isAnswered && item.answer ? (
                          <div className="mt-3 p-3 bg-primary-50/30 border border-primary-100 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[11px] font-bold text-primary-600 uppercase tracking-wider">관리자 답변</span>
                              <span className="text-[10px] text-achromatic-400">
                                {item.answeredAt ? new Date(item.answeredAt).toLocaleString('ko-KR') : ''}
                              </span>
                            </div>
                            <div className="text-[13px] text-achromatic-700 leading-relaxed whitespace-pre-wrap">{item.answer}</div>
                          </div>
                        ) : (
                          <div className="mt-3 p-3 bg-amber-50/30 border border-amber-100 rounded-lg">
                            <div className="text-[12px] text-amber-600 font-medium">답변 대기 중입니다.</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactSettingComponent;
